import { ManagerPhone } from './ManagerPhone'
import { MemberPhone } from './MemberPhone'
import { FRAME_H, FRAME_RADIUS, FRAME_W, PhoneCaption } from './PhoneShell'
import { Sidebar } from './Sidebar'
import { usePhoneScale } from '../lib/usePhoneScale'
import { sx } from '../lib/sx'
import type { ArchitectureId } from '../demo/architecture'
import type { Vals } from '../demo/vals'

/** Control sidebar on the left, one or two handsets filling the rest. */
export function DemoStage({
  v,
  architecture,
  onArchitectureChange,
}: {
  v: Vals
  architecture: ArchitectureId
  onArchitectureChange: (id: ArchitectureId) => void
}) {
  const { ref, scale } = usePhoneScale(2, FRAME_W, FRAME_H)

  return (
    <div
      className="dark"
      style={sx(
        'height:100vh;width:100%;overflow:hidden;background:#0b0b0c;color:var(--text-primary);' +
          'font-family:var(--font-sans);padding:24px 28px;display:flex;gap:32px;align-items:stretch'
      )}
    >
      <Sidebar v={v} architecture={architecture} onArchitectureChange={onArchitectureChange} />

      <div
        ref={ref}
        style={sx(
          'flex:1;min-width:0;display:flex;gap:36px;align-items:flex-start;justify-content:center'
        )}
      >
        <div style={sx('display:flex;flex-direction:column;gap:12px')}>
          <PhoneCaption name="Michael · group owner" dotColor="var(--bg-accent)" />
          <ManagerPhone v={v} scale={scale} />
          <Caption>{v.managerCaption}</Caption>
        </div>

        {v.showMember ? (
          <div style={sx('display:flex;flex-direction:column;gap:12px')}>
            <PhoneCaption name="Vito · member" dotColor="var(--chart-6)" />
            <MemberPhone v={v} scale={scale} />
            <Caption>{v.memberCaption}</Caption>
          </div>
        ) : (
          <EmptyPhoneSlot scale={scale} />
        )}
      </div>
    </div>
  )
}

function Caption({ children }: { children: string }) {
  return (
    <div
      style={sx(
        `width:${Math.round(FRAME_W * 0.62)}px;max-width:100%;height:34px;overflow:hidden;` +
          `font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)`
      )}
    >
      {children}
    </div>
  )
}

/** Flow 1 has no second handset yet — hold the space rather than reflow. */
function EmptyPhoneSlot({ scale }: { scale: number }) {
  return (
    <div style={sx('display:flex;flex-direction:column;gap:12px')}>
      <PhoneCaption name="N/A · member" dotColor="var(--bg-disabled)" muted />
      <div
        style={sx(
          `width:${Math.round(FRAME_W * scale)}px;height:${Math.round(FRAME_H * scale)}px;flex:none;` +
            // Same silhouette as a real handset, so the empty slot reads as a
            // missing phone rather than a generic box.
            `border-radius:${Math.round(FRAME_RADIUS * scale)}px;` +
            `border:1px dashed var(--border-secondary);` +
            `display:flex;flex-direction:column;align-items:center;justify-content:center;` +
            `gap:10px;text-align:center;padding:0 28px`
        )}
      >
        <span
          style={sx(
            'width:28px;height:28px;background:var(--text-disabled);' +
              '-webkit-mask:url(/assets/icons/phone-apple.svg) center/contain no-repeat;' +
              'mask:url(/assets/icons/phone-apple.svg) center/contain no-repeat'
          )}
        />
        <span
          style={sx(
            'font-size:var(--fs-sm);font-weight:var(--fw-medium);color:var(--text-secondary)'
          )}
        >
          No members yet
        </span>
        <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-disabled)')}>
          A member device joins the demo in Flow 2, once Michael sends an invite.
        </span>
      </div>
      <div
        style={sx(
          `width:${Math.round(FRAME_W * 0.62)}px;max-width:100%;height:34px;overflow:hidden;` +
            `font-size:var(--fs-xs);line-height:17px;color:var(--text-disabled)`
        )}
      >
        No second device in this flow.
      </div>
    </div>
  )
}
